import { ApiError } from "@allergy123/api-client";
import { orderStatusLabel } from "./format";

const MESSAGES: Record<string, string> = {
  not_found: "Not found. It may belong to another practice or location, or it was removed.",
  forbidden: "Your membership doesn't allow this.",
  mfa_required: "Two-step verification is required for clinical records. Sign in with it and try again.",
  unauthenticated: "Your session has ended. Sign in again.",
  clinical_unavailable: "The clinical API needs the database. Start the API with npm run api:dev:db.",
  service_unavailable: "The record could not be audited, so nothing was returned. Try again in a moment.",
  version_conflict: "Someone else changed this record first. It has been reloaded; check it and try again.",
  not_draft: "This encounter is no longer a draft.",
  already_exists: "That already exists.",
  stale_command: "This change was made more than 30 days ago on a device that was offline, so it was set aside for review instead of applied.",
  idempotency_conflict: "A different change was already sent with the same request key. Reload and try again.",
  invalid_json: "The request couldn't be read. Reload the page and try again.",
  request_too_large: "That is too large to send.",
  not_ready: "The document is still being prepared.",
  invalid_test: "Every allergen read 0 mm while the saline control reacted, so this cannot be recorded as a negative test. Check the readings.",
  confirm_required: "This submission needs a confirmation first.",
  internal: "Something went wrong on the server.",
  // Billing (ADR 0008): plain sentences for the practice; payment methods and prices are set up by Allergy 123.
  no_billing_account: "This practice has no billing account yet. Ask Allergy 123 to set one up before ordering.",
  no_payment_method: "Ask Allergy 123 to add a payment method before ordering.",
  account_not_active: "This practice's billing account is not active. Contact Allergy 123.",
  custom_plan_order_by_staff: "Your plan is billed by Allergy 123 staff; contact them to order.",
  price_not_configured: "This product has no price set up for your plan yet. Contact Allergy 123.",
  billing_unavailable: "Billing is not available right now. Try again later.",
  supplies_unavailable: "Supply balances need the API in database mode. Start the API with npm run api:dev:db.",
};

const VIOLATIONS: Record<string, string> = {
  too_many_components: "At most five antigens can be ordered.",
  unknown_antigen: "One of the antigens is not on this panel.",
  duplicate_component: "An antigen was picked twice.",
  no_reaction: "Only antigens with a reaction can be ordered.",
  control_not_selectable: "Controls are never part of an order.",
  environmental_mixed_with_other: "Environmental antigens cannot be mixed with other groups.",
};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const list = (v: unknown): Record<string, unknown>[] => (Array.isArray(v) ? v.filter(isRecord) : []);

/** The items an `incomplete` refusal names, as short sentences. */
export function missingItems(err: unknown): string[] {
  if (!(err instanceof ApiError) || err.code !== "incomplete") return [];
  return list(err.details.missing).map((m) => String(m.message ?? m.key ?? ""));
}

export function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "incomplete") {
      const items = missingItems(err);
      return items.length ? `Not complete yet: ${items.join(" ")}` : "Not complete yet.";
    }
    if (err.code === "invalid_selection") return list(err.details.violations).map((v) => VIOLATIONS[String(v.code)] ?? String(v.code)).join(" ") || "That selection is not allowed.";
    if (err.code === "invalid_transition") {
      const reason = typeof err.details.reason === "string" ? err.details.reason : undefined;
      const status = typeof err.details.status === "string" ? err.details.status : undefined;
      const why: Record<string, string> = {
        no_result: "The test has no result yet.",
        not_a_test: "Only a test or retest can have treatment ordered or declined.",
        declined: "Treatment was declined for this test.",
        order_exists: "An order already exists for this test.",
        already_declined: "Treatment was already declined.",
        not_yet_signable: "That signature comes after the test is submitted.",
        no_published_panel: "This location has no published tray panel and profile.",
        no_published_form: "No published form exists for this visit kind.",
        supersedes_not_submitted: "Only a submitted order can be replaced.",
      };
      return (reason && why[reason]) ?? `That isn't possible while the record is ${status ?? "in its current state"}.`;
    }
    if (err.code === "invalid_request") return err.field ? `Check ${err.field}.` : "Some of the information isn't valid.";
    return MESSAGES[err.code] ?? err.code;
  }
  return err instanceof Error ? err.message : "Something went wrong";
}

/** A supply-order refusal in the practice's words: a cancel after staff moved the order on names the order's state, everything else is `errorText`. */
export function orderErrorText(err: unknown): string {
  if (err instanceof ApiError && err.code === "invalid_transition") {
    const status = typeof err.details.status === "string" ? err.details.status : undefined;
    return status ? `This order is already ${orderStatusLabel(status).toLowerCase()}, so it can no longer be cancelled here. Contact Allergy 123.` : "This order can no longer be cancelled here. Contact Allergy 123.";
  }
  return errorText(err);
}

export const isConflict = (err: unknown): boolean => err instanceof ApiError && err.code === "version_conflict";
/** The local API in memory mode answers `503 supplies_unavailable`; the billing provider missing is `503 billing_unavailable`. */
export const isUnavailable = (err: unknown): boolean => err instanceof ApiError && (err.code === "supplies_unavailable" || err.code === "billing_unavailable");
export const isForbidden = (err: unknown): boolean => err instanceof ApiError && err.code === "forbidden";
export const isNotFound = (err: unknown): boolean => err instanceof ApiError && err.status === 404;
